import type { CookieValue, UserPreferences } from 'vanilla-cookieconsent';
import { buildGoogleConsentState, type GoogleConsentState } from './consent-mode';

/**
 * Consent audit actions stored on consent_records.action
 * (docs/DATA-MODEL.md §consent_records).
 *
 * Client auto-emits: initial | accept_all | reject_all | save_preferences.
 * `withdraw` is reserved for an explicit future UI path — not derived from
 * library AcceptType alone.
 */
export type ConsentLogAction =
  | 'initial'
  | 'accept_all'
  | 'reject_all'
  | 'save_preferences'
  | 'withdraw';

/** POST /api/client/consent body — docs/PRODUCT.md §7, no IP/UA. */
export type ConsentLogPayload = {
  siteId: string;
  anonymousConsentId: string;
  configurationVersion: string;
  action: ConsentLogAction;
  categories: string[];
  services: Record<string, string[]>;
  googleConsentState: GoogleConsentState;
  language: string;
};

/** Narrow CC surface needed to assemble a consent log payload. */
export type ConsentLogCookieConsentApi = {
  getCookie(): CookieValue;
  getUserPreferences(): UserPreferences;
  acceptedCategory(categoryName: string): boolean;
};

/**
 * Map library AcceptType → consent_records.action for preference changes.
 * AcceptType is only 'all' | 'custom' | 'necessary' (types/index.d.ts).
 * Used for onChange only — onFirstConsent always logs as 'initial'.
 */
export function actionFromAcceptType(
  acceptType: UserPreferences['acceptType'],
): Exclude<ConsentLogAction, 'initial' | 'withdraw'> {
  if (acceptType === 'all') return 'accept_all';
  if (acceptType === 'necessary') return 'reject_all';
  return 'save_preferences';
}

/**
 * Fire-and-forget POST of a consent decision.
 * Network failures never throw to the host page (console.warn only).
 *
 * Call from onFirstConsent (action='initial') and onChange (mapped action).
 * Do NOT call from onConsent — that fires on every page load with valid
 * consent and would duplicate audit rows (docs/cookieconsent-3.1.0/docs/
 * advanced/consent-logging.md; ARCHITECTURE.md §8).
 */
export function sendConsentLog(
  action: ConsentLogAction,
  apiBaseUrl: string,
  siteId: string,
  configurationVersion: string,
  ccApi: ConsentLogCookieConsentApi,
  securityStorageDefault: 'granted' | 'denied' = 'denied',
): void {
  try {
    const cookie = ccApi.getCookie();
    const preferences = ccApi.getUserPreferences();

    const payload: ConsentLogPayload = {
      siteId,
      anonymousConsentId: cookie.consentId,
      configurationVersion,
      action,
      categories: preferences.acceptedCategories,
      services: preferences.acceptedServices,
      googleConsentState: buildGoogleConsentState(
        (category) => ccApi.acceptedCategory(category),
        securityStorageDefault,
      ),
      language: cookie.languageCode,
    };

    void fetch(`${apiBaseUrl}/api/client/consent`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      // keepalive helps the request survive page navigations after banner click
      keepalive: true,
    }).catch((err: unknown) => {
      console.warn('[Yargı CMP] Consent log POST failed:', err);
    });
  } catch (err: unknown) {
    // getCookie/getUserPreferences threw or payload assembly failed
    console.warn('[Yargı CMP] Consent log skipped:', err);
  }
}
